import {
	type Key,
	Tab as RACTab,
	TabList as RACTabList,
	TabPanel as RACTabPanel,
	Tabs as RACTabs,
	type TabListProps,
	type TabPanelProps,
	type TabProps,
	type TabsProps,
	composeRenderProps
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cn } from '../utils';

const tabPanelStyles = tv({
	base: 'flex-1 rounded-b-lg bg-gray-100 text-sm text-gray-900'
});

function TabPanel(props: TabPanelProps) {
	return (
		<RACTabPanel
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) => tabPanelStyles({ ...renderProps, className }))}
		/>
	);
}

function TabPanels({ children }: { children: React.ReactNode }) {
	return <div className="p-4">{children}</div>;
}

export const baseTabStyles = tv({
	base: 'flex cursor-default items-center whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors duration-100',
	variants: {
		isSelected: {
			true: 'border-primary-500 text-primary-600',
			false: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
		},
		isDisabled: {
			true: 'cursor-not-allowed text-gray-300 opacity-50'
		}
	}
});

function Tab(props: TabProps) {
	return (
		<RACTab
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) => baseTabStyles({ ...renderProps, className }))}
		/>
	);
}

const tabListStyles = tv({
	base: 'flex items-center gap-0 overflow-x-auto border-b border-gray-200',
	variants: {
		orientation: {
			horizontal: 'flex-row',
			vertical: 'flex-col items-start border-b-0 border-r'
		}
	}
});

function TabList<T extends object>(props: TabListProps<T>) {
	return (
		<div className={cn('rounded-t-lg bg-white px-4', props.className)}>
			<RACTabList
				{...props}
				className={composeRenderProps(props.className, (className, renderProps) => tabListStyles({ ...renderProps, className }))}
			/>
		</div>
	);
}

const tabsStyles = tv({
	base: 'rounded-lg border border-gray-200 bg-gray-100',
	variants: {
		orientation: {
			horizontal: 'flex-col',
			vertical: 'w-[800px] flex-row'
		}
	}
});
export function Tabs(props: Omit<TabsProps, 'onSelectionChange'> & { onSelectionChange?: (newTab: string) => void }) {
	const onSelectionChange = props.onSelectionChange ? (key: Key) => props.onSelectionChange?.(key.toString()) : undefined;
	return (
		<RACTabs
			{...props}
			onSelectionChange={onSelectionChange}
			className={composeRenderProps(props.className, (className, renderProps) => tabsStyles({ ...renderProps, className }))}
		/>
	);
}

Tabs.Tab = Tab;
Tabs.TabList = TabList;
Tabs.TabPanel = TabPanel;
Tabs.TabPanels = TabPanels;
